import { Component, OnInit } from "@angular/core";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { SpecialUserPermissions } from "@enums/SpecialUserPermissions";
import { AccountUserType } from "@enums/UserType";
import { IAccountUserMapping, IUser } from "@interfaces/IUser";
import { AccountService, IAccount } from "@services/account.service";
import { AccountFeatureService, IAccountFeatureDescription, ISpecialUserPermissionDescription } from "@services/accountFeature.service";
import { CurrentUserService } from "@services/currentUser.service";
import "./user-access-tester.component.scss";

@Component({
    selector: "user-access-tester",
    template: require("./user-access-tester.component.html")
})
export class UserAccessTesterComponent implements OnInit {

    // raw data
    user: IUser;
    accountData: IAccount;
    accountFeatureDescriptions: IAccountFeatureDescription[];
    specialUserPermissionDescriptions: ISpecialUserPermissionDescription[];

    // state
    cachedUserType: AccountUserType;
    cachedReadFeatures: AccountFeatures;
    cachedManageFeatures: AccountFeatures;
    cachedSpecialUserPermissions: SpecialUserPermissions;

    // spinners
    loading: boolean;

    // enums
    UserType = AccountUserType;
    AccountFeature = AccountFeatures;

    get isAdminUser(): boolean {
        return this.user?.CurrentAccountUserMapping?.Type === AccountUserType.Admin;
    }

    constructor(
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly currentUserService: CurrentUserService
    ) { }

    ngOnInit(): void {
        this.getData();
    }

    reset(): void {
        this.getData(true);
    }

    toggleUserType(): void {
        this.user.CurrentAccountUserMapping.Type = (this.user.CurrentAccountUserMapping.Type === AccountUserType.Admin) ? AccountUserType.User : AccountUserType.Admin;

        this.cacheUserType();
        this.processUserType();
    }

    toggleAccountFeature(feature: AccountFeatures): void {
        this.accountService.updateAccountCache(this.accountData);
        this.accountFeatureService.printAccountFeatureListForAccount(this.accountData);
    }

    toggleUserReadFeature(feature: AccountFeatures): void {
        this.currentUserService.updateCurrentUserCache(this.user);
        this.cacheFeatureAccess();
    }

    toggleUserManageFeature(feature: AccountFeatures): void {
        this.currentUserService.updateCurrentUserCache(this.user);
        this.cacheFeatureAccess();
    }

    toggleSpecialUserPermission(permission: SpecialUserPermissions): void {
        this.currentUserService.updateCurrentUserCache(this.user);
        this.cacheSpecialPermissionAccess();
    }

    updateFeatureSet(): void {
        if (this.loading) {
            return;
        }

        this.loading = true;

        this.accountService.updateAccountData().then((accountData: IAccount) => {
            this.accountData = accountData;

        }).finally(() => {
            this.loading = false;
        });
    }

    // ---

    private getData(invalidateCache: boolean = false): Promise<void> {
        if (this.loading) {
            return;
        }

        this.loading = true;

        const currentUserPromise = this.currentUserService.getCurrentUser(invalidateCache) as Promise<IUser>;
        const accountDataPromose = this.accountService.getAccountData(invalidateCache) as Promise<IAccount>;

        return Promise.all([currentUserPromise, accountDataPromose]).then(([currentUser, accountData]) => {
            this.user = currentUser;
            this.accountData = accountData;
            this.accountFeatureDescriptions = this.accountFeatureService.getFeatureDescriptionsForAccountAndUser(accountData, currentUser);
            this.specialUserPermissionDescriptions = this.accountFeatureService.getSpecialUserPermissionDescriptions();

            this.cacheUserType();
            this.cacheFeatureAccess();
            this.cacheSpecialPermissionAccess();
            this.processUserType();

        }).finally(() => {
            this.loading = false;
        });
    }

    private setAllReadFeatures(enabled: boolean): void {
        this.accountFeatureDescriptions.forEach(featureDescription => {
            enabled
                ? this.addUserReadFeature(this.user.CurrentAccountUserMapping, featureDescription.feature)
                : this.removeUserReadFeature(this.user.CurrentAccountUserMapping, featureDescription.feature);
        });
    }

    private setAllManageFeatures(enabled: boolean): void {
        this.accountFeatureDescriptions.forEach(featureDescription => {
            enabled
                ? this.addUserManageFeature(this.user.CurrentAccountUserMapping, featureDescription.feature)
                : this.removeUserManageFeature(this.user.CurrentAccountUserMapping, featureDescription.feature);
        });
    }

    private cacheUserType(): void {
        this.cachedUserType = this.user.CurrentAccountUserMapping.Type;
    }

    private cacheFeatureAccess(): void {
        this.cachedReadFeatures = this.user.CurrentAccountUserMapping.ReadFeatures;
        this.cachedManageFeatures = this.user.CurrentAccountUserMapping.ManageFeatures;
    }

    private cacheSpecialPermissionAccess(): void {
        this.cachedSpecialUserPermissions = this.user.CurrentAccountUserMapping.SpecialUserPermissions;
    }

    private addUserReadFeature(userMapping: IAccountUserMapping, feature: AccountFeatures): void {
        userMapping.ReadFeatures = userMapping.ReadFeatures | feature;
    }

    private removeUserReadFeature(userMapping: IAccountUserMapping, feature: AccountFeatures): void {
        userMapping.ReadFeatures = userMapping.ReadFeatures & ~feature;
    }

    private addUserManageFeature(userMapping: IAccountUserMapping, feature: AccountFeatures): void {
        userMapping.ManageFeatures = userMapping.ManageFeatures | feature;
    }

    private removeUserManageFeature(userMapping: IAccountUserMapping, feature: AccountFeatures): void {
        userMapping.ManageFeatures = userMapping.ManageFeatures & ~feature;
    }

    private processUserType(): void {
        if (this.isAdminUser) {
            this.setAllReadFeatures(true);
            this.setAllManageFeatures(true);
        } else {
            this.user.CurrentAccountUserMapping.ReadFeatures = this.cachedReadFeatures;
            this.user.CurrentAccountUserMapping.ManageFeatures = this.cachedManageFeatures;
        }

        this.currentUserService.updateCurrentUserCache(this.user);
    }
}
